import axios from "axios";
import { refreshToken } from "../../application/actions/authActions";
import store from "../../application/store";
import {
  isPublicAuthApiUrl,
  readStoredUser,
  redirectToLogin,
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
} from "../utils/authSession";

const baseURL = window.globalConfig?.API_URL;

export const authHeader = () => {
  const accessToken = getAccessToken(readStoredUser());
  if (accessToken) {
    return { Authorization: "Bearer " + accessToken };
  }
  return {};
};

export const getLocalRefreshToken = () => {
  return getRefreshToken(readStoredUser()) || "";
};

export const api = axios.create({
  baseURL: baseURL,
});

const CORRELATION_HEADER = "X-Correlation-Id";
const CORRELATION_STORAGE_KEY = "solarentCorrelationId";

function getOrCreateCorrelationId() {
  try {
    let id = sessionStorage.getItem(CORRELATION_STORAGE_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(CORRELATION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};

  if (!config.headers[CORRELATION_HEADER]) {
    config.headers[CORRELATION_HEADER] = getOrCreateCorrelationId();
  }

  const requestUrl = config.url || "";
  if (!isPublicAuthApiUrl(requestUrl) && !config.headers.Authorization) {
    const accessToken = getAccessToken(readStoredUser());
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  // FormData must keep browser-generated multipart boundary; never force JSON/content-type.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    const headers = config.headers;
    // Never leave Content-Type as the string "false" — that breaks ASP.NET multipart binding.
    if (headers && typeof headers.set === "function") {
      try {
        headers.set("Content-Type", undefined);
      } catch (_) {
        /* ignore */
      }
      if (typeof headers.delete === "function") {
        headers.delete("Content-Type");
        headers.delete("content-type");
      }
    } else if (headers) {
      delete headers["Content-Type"];
      delete headers["content-type"];
    }
  }

  return config;
});

function requestHadAuthHeader(config) {
  return Boolean(config?.headers?.Authorization);
}

function createAuthError(message = "Authentication failed") {
  const authError = new Error(message);
  authError.isAuthError = true;
  return authError;
}

const errorHandler = async (error) => {
  const statusCode = error.response?.status;
  const originalConfig = error.config ?? {};

  if (error.code === "ERR_CANCELED") {
    return Promise.resolve();
  }

  // CORS / network — never clear session
  if (!error.response) {
    return Promise.reject(error);
  }

  if (isPublicAuthApiUrl(originalConfig.url)) {
    return Promise.reject(error);
  }

  if (
    originalConfig.url !== "account/RefreshToken" &&
    statusCode === 401 &&
    !originalConfig._retry
  ) {
    const storedUser = readStoredUser();

    if (!requestHadAuthHeader(originalConfig) || !getAccessToken(storedUser)) {
      return Promise.reject(error);
    }

    // Token still looks valid — don't force logout (endpoint/auth mismatch)
    if (!isAccessTokenExpired(storedUser)) {
      return Promise.reject(error);
    }

    if (!getRefreshToken(storedUser)) {
      redirectToLogin({
        source: "missing-refresh-token",
        url: originalConfig.url,
      });
      return Promise.reject(createAuthError());
    }

    originalConfig._retry = true;
    try {
      const result = await store.dispatch(refreshToken()).unwrap();
      if (result?.user?.accessToken) {
        originalConfig.headers = originalConfig.headers ?? {};
        originalConfig.headers.Authorization = `Bearer ${result.user.accessToken}`;
        return api(originalConfig);
      }

      redirectToLogin({
        source: "refreshToken-empty",
        url: originalConfig.url,
      });
      return Promise.reject(createAuthError());
    } catch (refreshError) {
      if (refreshError?.response?.status === 401) {
        redirectToLogin({
          source: "refreshToken-failed",
          url: originalConfig.url,
        });
      }
      return Promise.reject(
        refreshError?.isAuthError ? refreshError : createAuthError()
      );
    }
  }

  if (originalConfig.url === "account/RefreshToken" && statusCode === 401) {
    redirectToLogin({ source: "refreshToken-endpoint" });
    return Promise.reject(createAuthError());
  }

  return Promise.reject(error);
};

api.interceptors.response.use(undefined, (error) => {
  return errorHandler(error);
});

export default function setupAxios(axios, store, props) {}
